import { Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useMapContext } from '@resistapp/client/contexts/map-context';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { useMetricAndLevel } from '@resistapp/client/contexts/use-overview-context/hooks/use-metric-and-level';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import { L2Target } from '@resistapp/common/assays';
import {
  AllProjectSampleTypesGroup,
  ComparableSampleGroupType,
  getComparableSamplingPointGroups,
  getOrderedSampleGroupTypesForMetric,
  SampleTypeGroup,
} from '@resistapp/common/comparable-sample-groups';
import { TestSelectors } from '@resistapp/common/testing/visual-test-selectors';
import { MetricMode, ProcessMode } from '@resistapp/common/types';
import { compact } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { ArrowIcon } from '../icons/arrow-icon';
import { CloseIconSimple } from '../icons/close-icon';
import { theme } from '../shared/theme';
import { ResistanceBoxes } from './resistance-boxes';
import { SamplingPointTitle, SamplingPointTitleSmall } from './sampling-point-titles';
import { ScopeSelectors } from './scope-selectors';
import { allAntibiotics, OptionBase, OptionForTargets } from './types';

export function OverviewFocusHeader() {
  const {
    selectedSiteDatum,
    zoomedMapData,
    previousAdminAreasLifo,
    metricMode,
    defaultProcessMode,
    mapDataByLevel,
    levelsWithZoomableAreas,
  } = useOverviewContext();
  const { changeZoomedAdminAreaStable } = useMapContext();
  const { queryFilters } = useSampleDataContext();

  const topLevelMapData =
    mapDataByLevel && levelsWithZoomableAreas?.length ? mapDataByLevel[levelsWithZoomableAreas[0]][0] : undefined;

  // TOOD FIX THIS TO SHOW THE METRICS CORRECTLY
  const { toggleSamplingPointStable: toggleSamplingPoint } = queryFilters;
  const topLevelSamplingPoint = topLevelMapData?.samplingPoint;
  const selectedOrZoomedDatum = selectedSiteDatum || zoomedMapData;
  const selectedOrZoomedSamplingPoint = selectedOrZoomedDatum?.samplingPoint;

  const { metric: metricTopAdminLevel, level: levelTopAdminLevel } = useMetricAndLevel(topLevelMapData);
  const { metric: metricBefore, level: levelBefore } = useMetricAndLevel(selectedOrZoomedDatum, {
    processMode: ProcessMode.BEFORE,
  });
  const { metric: metricAfter, level: levelAfter } = useMetricAndLevel(selectedOrZoomedDatum, {
    processMode: ProcessMode.AFTER,
  });
  const { metric: primaryMetric, level: primaryLevel } = useMetricAndLevel(selectedOrZoomedDatum, {});
  const selectedSmplPntGroupHasData = Boolean(metricAfter || metricBefore || primaryMetric);

  const groupToSelect = useMemo(() => {
    const groupsWithData = selectedSiteDatum
      ? getComparableSamplingPointGroups(
          compact([selectedSiteDatum.samplingPoint, selectedSiteDatum.samplingPointAfter]),
          metricMode,
        ).filter(g => g.type !== AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS)
      : undefined;
    return groupsWithData
      ? getOrderedSampleGroupTypesForMetric(metricMode).find(type => groupsWithData.some(g => g.type === type))
      : undefined;
  }, [selectedSiteDatum, metricMode]);

  const selectedSampleTypeGroup = queryFilters.filters.selectedSampleTypeGroup;
  // This controls the smplPnt type selector open state. It should be open by default only when there is no data. And only when there is a site selected.
  const [envTypeSelectorOpen, setSampleTypeSelectorOpen] = useState(false);
  useEffect(() => {
    if (!selectedSiteDatum || !groupToSelect) {
      return;
    }

    // Add a small delay to ensure data is properly loaded based on the changed query filters
    const timeoutId = setTimeout(() => {
      // When in REDUCTION and TREATED_WASTEWATER is selected, show the smplPnt type selector
      const newSampleTypeSelectorOpen =
        !selectedSmplPntGroupHasData &&
        (metricMode !== MetricMode.REDUCTION || groupToSelect === ComparableSampleGroupType.WATER_TREATMENT);
      setSampleTypeSelectorOpen(newSampleTypeSelectorOpen);
    }, 100); // 100ms delay should be enough for URL data to load

    return () => {
      clearTimeout(timeoutId);
    };
  }, [selectedSmplPntGroupHasData, selectedSiteDatum, groupToSelect, metricMode, selectedSampleTypeGroup]);

  const hasBeforeAbundances = !!selectedOrZoomedDatum?.beforeAbundances;
  const hasAfterAbundances = !!selectedOrZoomedDatum?.afterAbundances;

  const firstLevelLabel = metricMode === MetricMode.REDUCTION ? 'Change' : hasBeforeAbundances ? 'Raw' : undefined;
  const secondLevelLabel = metricMode !== MetricMode.REDUCTION && hasAfterAbundances ? 'Treated' : undefined;

  const previousAdminArea =
    previousAdminAreasLifo.length > 0 && previousAdminAreasLifo[previousAdminAreasLifo.length - 1];

  const handleAntibioticChange = (value: OptionForTargets | null) => {
    const correctValue = !value || value.value === allAntibiotics ? queryFilters.filters.selectedTargets : value.value;
    if (correctValue === allAntibiotics || !value) {
      queryFilters.toggleGeneGroupStable(correctValue as L2Target, true);
    } else {
      queryFilters.toggleSingleTarget(value.value);
    }
  };
  const handleSampleTypeGroupChange = (value: string | undefined) => {
    const correctValue = !value ? selectedSampleTypeGroup : value;
    queryFilters.setSampleTypeGroupStable(correctValue as SampleTypeGroup, false);
  };

  const TopLevelResistanceBoxes = (
    <ResistanceBoxes
      beforeOrChange={
        defaultProcessMode === ProcessMode.BEFORE
          ? {
              metric: metricTopAdminLevel,
              level: levelTopAdminLevel,
              label: undefined,
            }
          : undefined
      }
      after={
        defaultProcessMode === ProcessMode.AFTER
          ? {
              metric: metricTopAdminLevel,
              level: levelTopAdminLevel,
              label: undefined,
            }
          : undefined
      }
      handleSampleTypeGroupChange={handleSampleTypeGroupChange}
      groupToSelect={groupToSelect}
    />
  );

  // SPECS: How header and site process markers should behave render each level
  // https://docs.google.com/spreadsheets/d/1SDXnN0LI8_hNqwNgDlpKJtCFL_JiPif_5KxBQgW2e90/edit?gid=0#gid=0

  return (
    <Container className={TestSelectors.TEST_OVERVIEW_FOCUS_HEADER} flexDirection={{ lg: 'row' }}>
      {/* SMALL TOP LEVEL SAMPLING POINT BOX - WHEN SOMEHTING IS SELECTED */}
      {topLevelMapData &&
        selectedOrZoomedSamplingPoint &&
        selectedOrZoomedSamplingPoint.name !== topLevelSamplingPoint?.name && (
          <BorderContainerSmall height={{ md: '128px' }} display={{ base: 'none', lg: 'flex' }}>
            <SamplingPointTitleSmall environmentName={topLevelSamplingPoint?.name || ''} />
            {TopLevelResistanceBoxes}
          </BorderContainerSmall>
        )}
      {/* BIG TOP LEVEL SAMPLING POINT BOX - WHEN NO AREA OR SITE IS SELECTED */}
      {!selectedOrZoomedSamplingPoint && (
        <BorderContainerBig
          flexDirection={{ md: 'row' }}
          height={{ md: '128px' }}
          gap={{ base: theme.spacing[6], lg: theme.spacing[12] }}
        >
          <SamplingPointTitle environmentName={topLevelSamplingPoint?.name || ''} subtype="pooled" />
          {TopLevelResistanceBoxes}
          <ScopeSelectors
            onChangeAntibiotic={
              handleAntibioticChange as unknown as (value: OptionBase | OptionForTargets | null) => void
            }
            onChangeSampleTypeGroup={handleSampleTypeGroupChange}
            selectedTargets={queryFilters.filters.selectedTargets}
            envTypeSelectorOpen={envTypeSelectorOpen}
            setSampleTypeSelectorOpen={setSampleTypeSelectorOpen}
          />
        </BorderContainerBig>
      )}

      {selectedOrZoomedSamplingPoint && (
        <>
          {/* > BREADCRUMB ARROWS > */}
          {previousAdminAreasLifo.length > 0 && (
            <Flex
              sx={{
                height: { base: '25px', lg: 'auto' },
                transform: { base: 'rotate(90deg)', lg: 'none' },
                alignSelf: 'center',
                width: 'min-content',
              }}
              display={{ base: 'none', lg: 'flex' }}
            >
              {previousAdminAreasLifo.map(area => (
                <ArrowIcon
                  direction="right"
                  style={{ color: theme.colors.neutral400, width: '14px' }}
                  key={area.name}
                />
              ))}
            </Flex>
          )}
          {/* SELECTED SITE OR ZOOMED SAMPLING POINT BOX */}
          <BorderContainerBig
            flexDirection={{ md: 'row' }}
            height={{ md: '128px' }}
            gap={{ base: theme.spacing[6], lg: theme.spacing[12] }}
          >
            <SamplingPointTitle
              environmentName={selectedOrZoomedSamplingPoint.name || ''}
              subtype={selectedOrZoomedSamplingPoint.subtype || 'pooled'}
            />
            <ResistanceBoxes
              beforeOrChange={{
                metric: metricMode === MetricMode.REDUCTION ? primaryMetric : metricBefore,
                level: metricMode === MetricMode.REDUCTION ? primaryLevel : levelBefore,
                label: firstLevelLabel,
              }}
              after={
                metricMode !== MetricMode.REDUCTION && hasAfterAbundances
                  ? {
                      metric: metricAfter,
                      level: levelAfter,
                      label: secondLevelLabel,
                    }
                  : undefined
              }
              handleSampleTypeGroupChange={handleSampleTypeGroupChange}
              groupToSelect={groupToSelect}
            />
            <ScopeSelectors
              onChangeAntibiotic={
                handleAntibioticChange as unknown as (value: OptionBase | OptionForTargets | null) => void
              }
              onChangeSampleTypeGroup={handleSampleTypeGroupChange}
              selectedTargets={queryFilters.filters.selectedTargets}
              envTypeSelectorOpen={envTypeSelectorOpen}
              setSampleTypeSelectorOpen={setSampleTypeSelectorOpen}
            />
            {selectedSiteDatum && (
              <CloseContainer>
                <CloseIconSimple
                  onClick={() => {
                    toggleSamplingPoint(undefined, true);
                    previousAdminArea && changeZoomedAdminAreaStable(previousAdminArea, { previous: true });
                  }}
                  style={{
                    color: theme.colors.neutral600,
                  }}
                />
              </CloseContainer>
            )}
          </BorderContainerBig>
        </>
      )}
    </Container>
  );
}

const Container = styled(Flex)`
  flex-direction: column;
  height: 100%;
  gap: ${theme.spacing[4]};
`;

const BorderContainer = styled(Flex)`
  position: relative;
  flex-direction: column;
  justify-content: start;
  align-items: center;
  height: 100%;
  width: 100%;
  padding: ${theme.spacing[6]} ${theme.spacing[8]};
  ${theme.addBorder(300, 'small')}
  background-color: white;
`;
const BorderContainerSmall = styled(BorderContainer)`
  justify-content: center;
  width: auto;
  gap: 13px;
`;
const BorderContainerBig = styled(BorderContainer)`
  padding-right: 0;
  padding-right: 65px;
`;

const CloseContainer = styled(Flex)`
  position: absolute;
  top: 0;
  right: 38px;
  height: 100%;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSize.h2};
`;
